import React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import _ from 'lodash';

import { FullPageLoader } from 'shared/components/loading';
import { MenuCustomizationLayout } from '../components/menu-customization-layout';
import { useGetMenuCustomization } from '../data-access';
import { useImageBannerForm } from './use-image-banner-form';
import { AddImageBanner } from './add-image-banner';
import { ImageBannerLimit } from './image-banner-limit';
import { ImageBannerList as ImageBannerListItems } from './image-banner-list/image-banner-list';

export function ImageBannerList(): JSX.Element | null {
  const { data, loading } = useGetMenuCustomization();
  const match = useRouteMatch<{ id: string }>('/dispensaries/:id');
  const baseUrl = match?.url;

  if (loading) {
    return <FullPageLoader />;
  }

  if (!data) {
    return null;
  }

  return <ImageBannerListContent data={data} baseUrl={baseUrl} />;
}

type ImageBannerListContentProps = {
  data: NonNullable<ReturnType<typeof useGetMenuCustomization>['data']>;
  baseUrl: string | undefined;
};

function ImageBannerListContent({ data, baseUrl }: ImageBannerListContentProps): JSX.Element {
  const history = useHistory();
  const {
    isDirty,
    imageBanners,
    isImageBannerLimitReached,
    handleRemoveBanner,
    onDragEnd,
    handlePublish,
    handleReset,
  } = useImageBannerForm({ data });

  function handleAddBanner(): void {
    history.push(`${baseUrl ?? ''}/customize/image-banner/add`);
  }

  function handleEditBanner(bannerId: string): void {
    history.push(`${baseUrl ?? ''}/customize/image-banner/edit/${bannerId}`);
  }

  const addComponent = <AddImageBanner disabled={isImageBannerLimitReached} addImageModal={handleAddBanner} />;

  return (
    <>
      <MenuCustomizationLayout
        headerText='Image Banner'
        subHeaderText='Display an image carousel above your menu.'
        isDirty={isDirty}
        handlePublish={() => void handlePublish()}
        handleReset={handleReset}
        actionComponent={addComponent}
      >
        {isImageBannerLimitReached && <ImageBannerLimit />}

        <ImageBannerListItems
          imageBanners={imageBanners}
          removeBanner={handleRemoveBanner}
          editBanner={handleEditBanner}
          onDragEnd={onDragEnd}
        />
      </MenuCustomizationLayout>
    </>
  );
}
